import type { CombinedError } from 'urql';

import { useTrackEventEffect } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

/**
 * The sweetpass account has been successfully viewed if the feature flag is enabled and the loyalty profile has loaded.
 * @see: https://sweetgreen.atlassian.net/wiki/spaces/CEL/pages/4608294913/Mixpanel+Events+for+Loyalty
 */
export const useSweetpassMembershipSuccessEvent = (
  props: UseSweetpassMembershipSuccessEventProps,
) => {
  const { isLoading, isEnabled, hasLoyaltyProfile } = props;

  // Feature flag is enabled and the loyalty profile has loaded.
  const skip = isLoading || !isEnabled || !hasLoyaltyProfile;

  useTrackEventEffect({
    name: 'sweetpass_account.view.success',
    skip,
  });
};

/**
 * The sweetpass account has been failed if the feature flag is disabled or the loyalty profile failed to load.
 * @see: https://sweetgreen.atlassian.net/wiki/spaces/CEL/pages/4608294913/Mixpanel+Events+for+Loyalty
 */
export const useSweetpassMembershipFailureEvent = (
  props: UseSweetpassMembershipFailureEvent,
) => {
  const { isLoading, isEnabled, hasLoyaltyProfile, loyaltyProfileError } =
    props;

  // Feature flag is disabled or the loyalty profile failed to load.
  const skip = isLoading || (isEnabled && hasLoyaltyProfile);

  const nonGraphError = !isEnabled
    ? 'Sweetpass Disabled'
    : 'Missing Loyalty Profile';

  useTrackEventEffect({
    name: 'sweetpass_account.view.failure',
    skip,
    payload: {
      systemError: getUrqlError(loyaltyProfileError) ?? nonGraphError,
    },
  });
};

// ─── Types ──────────────────────────────────────────────────────────────────

type UseSweetpassMembershipSuccessEventProps = {
  readonly isLoading: boolean;
  readonly isEnabled: boolean;
  readonly hasLoyaltyProfile: boolean;
};

type UseSweetpassMembershipFailureEvent = {
  readonly isLoading: boolean;
  readonly isEnabled: boolean;
  readonly hasLoyaltyProfile: boolean;
  readonly loyaltyProfileError?: CombinedError;
};
